import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api, { normalize } from '../services/api';
import {
  downloadModeloAgentesExcel,
  exportAgentesExcel,
  parseAgentesExcelFile,
} from '../services/agentesExcel';
import { formatCnpjCpf } from '../lanz-ui';
import CadastroDataTable from '../components/CadastroDataTable/CadastroDataTable';
import AgenteFormDialog from '../components/AgenteFormDialog';
import { richItemTemplate, richValueTemplate } from '../components/RichSelectOption';
import '../components/RichSelectOption.css';
import { buildFilialFilterOptions, labelEmpresaFilial } from '../utils/filtroHelpers';
import './EmpresaPage.css';
import './FiliaisPage.css';
import './AgentesPage.css';
import './nfse/NfseConsulta.css';

function AgentesPage() {
  const toast = useRef(null);
  const [agentes, setAgentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingAgente, setEditingAgente] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importando, setImportando] = useState(false);
  const [empresa, setEmpresa] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const [filtroFilialId, setFiltroFilialId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/empresas/me').then((r) => setEmpresa(normalize(r.data))).catch(() => setEmpresa(null));
    api.get('/filiais').then((r) => setFiliais(normalize(r.data))).catch(() => setFiliais([]));
  }, []);

  useEffect(() => {
    carregar();
  }, [filtroFilialId]);

  const carregar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroFilialId != null) params.filialId = filtroFilialId;
      const { data } = await api.get('/agentes', { params });
      setAgentes(normalize(data));
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar os agentes.' });
    } finally {
      setLoading(false);
    }
  };

  const filialFiltroOptions = useMemo(
    () => buildFilialFilterOptions(empresa, filiais, { incluirTodas: true }),
    [empresa, filiais]
  );

  const nomeEmpresaPrincipal = labelEmpresaFilial(empresa, 'Empresa');

  const abrirNovo = () => {
    setEditingAgente(null);
    setDialogVisible(true);
  };

  const abrirEdicao = (agente) => {
    setEditingAgente(agente);
    setDialogVisible(true);
  };

  const aoSalvarAgente = () => {
    setDialogVisible(false);
    carregar();
  };

  const excluir = (agente) => {
    confirmDialog({
      message: `Remover o agente "${agente.razaosocial}"?`,
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await api.delete(`/agentes/${agente.id}`);
          toast.current?.show({ severity: 'success', summary: 'Removido', detail: 'Agente removido com sucesso.' });
          carregar();
        } catch (error) {
          toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível remover o agente.' });
        }
      },
    });
  };

  const onEscolherExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const linhas = await parseAgentesExcelFile(file);
      const { data } = await api.post('/agentes/importar', { somenteValidar: true, linhas });
      setImportPreview({ resultado: normalize(data), linhas });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Falha ao ler/validar o Excel.',
      });
    } finally {
      event.target.value = '';
    }
  };

  const confirmarImportacao = async () => {
    if (!importPreview?.linhas?.length) return;
    setImportando(true);
    try {
      const { data } = await api.post('/agentes/importar', {
        somenteValidar: false,
        linhas: importPreview.linhas,
      });
      const r = normalize(data);
      toast.current?.show({
        severity: 'success',
        summary: 'Importação',
        detail: `${r.importadas || 0} agente(s) importado(s). ${r.invalidas || 0} linha(s) ignorada(s).`,
      });
      setImportPreview(null);
      carregar();
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao importar.' });
    } finally {
      setImportando(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'razaosocial', header: 'Razão social' },
      { field: 'fantasia', header: 'Fantasia' },
      { field: 'cnpjcpf', header: 'CNPJ/CPF', body: (r) => formatCnpjCpf(r.cnpjcpf || '', r.pessoa === 'Fisica' ? 'cpf' : 'cnpj') },
      { field: 'tipo', header: 'Tipo' },
      {
        field: 'filialId',
        header: 'Empresa / Filial',
        body: (r) => {
          if (!r.filialId) return nomeEmpresaPrincipal;
          const f = filiais.find((x) => x.id === r.filialId);
          return f?.fantasia || f?.razaosocial || `Filial #${r.filialId}`;
        },
        exportValue: (r) => {
          if (!r.filialId) return nomeEmpresaPrincipal;
          const f = filiais.find((x) => x.id === r.filialId);
          return f?.fantasia || f?.razaosocial || `Filial #${r.filialId}`;
        },
      },
      {
        field: 'pessoa',
        header: 'Tipo pessoa',
        body: (r) => (r.pessoa === 'Fisica' ? 'Física' : r.pessoa === 'Juridica' ? 'Jurídica' : r.pessoa || '—'),
        defaultVisible: false,
      },
      { field: 'email', header: 'E-mail' },
      { field: 'telefone', header: 'Telefone' },
      { field: 'logradouro', header: 'Logradouro', defaultVisible: false },
      { field: 'numero', header: 'Número', defaultVisible: false },
      { field: 'complemento', header: 'Complemento', defaultVisible: false },
      { field: 'bairro', header: 'Bairro', defaultVisible: false },
      { field: 'cidade', header: 'Cidade' },
      { field: 'uf', header: 'UF', style: { width: 70 } },
      { field: 'cep', header: 'CEP', defaultVisible: false },
      { field: 'inscricaoMunicipal', header: 'Inscrição municipal', defaultVisible: false },
      { field: 'inscricaoEstadual', header: 'Inscrição estadual', defaultVisible: false },
      { field: 'codigoIbgeMunicipio', header: 'IBGE', defaultVisible: false },
      {
        field: '_acoes',
        header: 'Ações',
        sortable: false,
        alwaysVisible: true,
        style: { width: 120 },
        body: (row) => (
          <div className="agentes-acoes">
            <Button icon="pi pi-pencil" rounded text onClick={() => abrirEdicao(row)} tooltip="Editar" />
            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => excluir(row)} tooltip="Excluir" />
          </div>
        ),
      },
    ],
    [filiais, nomeEmpresaPrincipal]
  );

  return (
    <div className="agentes-page">
      <Toast ref={toast} />
      <ConfirmDialog acceptLabel="Sim" rejectLabel="Não" />

      <div className="agentes-header">
        <h1>Clientes e fornecedores</h1>
        <div className="agentes-header-actions">
          <Button label="Modelo Excel" icon="pi pi-download" className="p-button-outlined" onClick={downloadModeloAgentesExcel} />
          <Button label="Importar Excel" icon="pi pi-upload" className="p-button-outlined" onClick={() => fileInputRef.current?.click()} />
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onEscolherExcel} />
          <Button label="Novo cadastro" icon="pi pi-plus" onClick={abrirNovo} />
        </div>
      </div>

      <div className="nfse-filtros agentes-filtros">
        <div className="nfse-filtro-field nfse-filtro-field-xl">
          <label>Empresa / Filial</label>
          <Dropdown
            value={filtroFilialId}
            options={filialFiltroOptions}
            onChange={(e) => setFiltroFilialId(e.value)}
            optionLabel="label"
            filter
            filterBy="search,razao,fantasia,cnpjcpf,label"
            itemTemplate={richItemTemplate}
            valueTemplate={richValueTemplate}
            panelClassName="rich-select-panel"
            placeholder="Todas"
            className="w-full"
            showClear
          />
        </div>
      </div>

      {importPreview && (
        <div className="agentes-import-preview">
          <strong>Validação prévia:</strong>{' '}
          {importPreview.resultado?.validas || 0} válida(s), {importPreview.resultado?.invalidas || 0} inválida(s)
          {importPreview.resultado?.erros?.length > 0 && (
            <ul>
              {importPreview.resultado.erros.slice(0, 10).map((e) => (
                <li key={`${e.linha}-${e.mensagem}`}>
                  Linha {e.linha}: {e.mensagem}
                </li>
              ))}
            </ul>
          )}
          <div className="agentes-import-actions">
            <Button label="Cancelar" className="p-button-text" onClick={() => setImportPreview(null)} />
            <Button
              label="Confirmar importação das válidas"
              icon="pi pi-check"
              loading={importando}
              disabled={!importPreview.resultado?.validas}
              onClick={confirmarImportacao}
            />
          </div>
        </div>
      )}

      <CadastroDataTable
        value={agentes}
        columns={columns}
        storageKey="agentes"
        loading={loading}
        title="Clientes e fornecedores"
        emptyMessage="Nenhum cliente ou fornecedor encontrado."
        globalFilterFields={['razaosocial', 'fantasia', 'cnpjcpf', 'tipo', 'email', 'telefone', 'cidade', 'uf']}
        toolbarExtra={
          <Button
            label="Exportar formato importação"
            icon="pi pi-download"
            className="p-button-text p-button-sm"
            onClick={() => exportAgentesExcel(agentes)}
            disabled={!agentes?.length}
          />
        }
      />

      <AgenteFormDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        onSaved={aoSalvarAgente}
        toastRef={toast}
        defaultFilialId={filtroFilialId}
        agente={editingAgente}
      />
    </div>
  );
}

export default AgentesPage;
